import type { CatalogEntry, SceneView } from '@bioforge/protocol';
import { Container, FillGradient, Graphics, Sprite, Text } from 'pixi.js';
import { SCENE_HEIGHT, SCENE_WIDTH, catalogArt, sceneArt } from './art/art.js';
import { BOSS_NAMES } from './labels.js';
import { StoryOverlay } from './overlays/story-overlay.js';
import { listen, type AppContext, type Scene } from './scene.js';
import { tr } from './story/story.js';
import { DesignFrame, currentFrame, screenBackground } from './ui/frame.js';
import { ScrollView } from './ui/scroll.js';
import { idle, microbeIdle } from './ui/motion.js';
import { artSprite, artTexture, ensureArt } from './ui/textures.js';
import { COLOR, textStyle } from './ui/theme.js';
import { UiButton, panel, speechBubble, vertical } from './ui/widgets.js';
import {
  KIND_KICKER,
  TABS,
  bossFacts,
  bossOf,
  listItems,
  microbeCare,
  openedCount,
  type EncyclopediaTab,
  type ListItem,
} from './view/encyclopedia-data.js';

/** Раскладка: список и статья рядом (макет) или одна под другой с прокруткой (телефон). */
interface Layout {
  readonly listX: number;
  readonly listY: number;
  readonly listW: number;
  readonly listH: number;
  readonly detailX: number;
  readonly detailY: number;
  readonly detailW: number;
  readonly detailH: number;
  readonly pad: number;
  /** Масштаб портрета статьи (круг 230 px на макете). */
  readonly portrait: number;
}

const WIDE: Layout = {
  listX: 60,
  listY: 108,
  listW: 540,
  listH: 752,
  detailX: 630,
  detailY: 108,
  detailW: 750,
  detailH: 752,
  pad: 32,
  portrait: 1,
};

const TALL: Layout = {
  listX: 22,
  listY: 150,
  listW: 676,
  listH: 470,
  detailX: 22,
  detailY: 636,
  detailW: 676,
  detailH: 624,
  pad: 24,
  portrait: 0.62,
};

const caps = (text: string, color: number) =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size: 12, display: true, weight: '800', color, letterSpacing: 1.4 }),
  });

/** Рисунки статей растрируются один раз за сессию, когда энциклопедию открывают впервые. */
async function loadArt(
  catalog: readonly CatalogEntry[],
  scenes: readonly SceneView[],
): Promise<void> {
  await Promise.all([
    ...catalog.map((entry) => ensureArt(`catalog-${entry.id}`, catalogArt(entry), 256)),
    ...scenes.map((scene) =>
      ensureArt(
        `scene-${scene.id}`,
        sceneArt(scene.frames[0]?.art ?? ''),
        SCENE_WIDTH,
        SCENE_HEIGHT,
      ),
    ),
  ]);
}

export interface EncyclopediaOptions {
  readonly ctx: AppContext;
  readonly reducedMotion: boolean;
  readonly onClose: () => void;
}

/**
 * Энциклопедия по макету «Энциклопедия · микробы и боссы» (SPEC.md 15.7 и 19.3): встреченные
 * статьи каталога по разделам и «Архив лаборатории» — просмотренные сцены, их можно пересмотреть.
 * Открывается из главного экрана и поверх боя из паузы.
 */
export class EncyclopediaScene implements Scene {
  readonly view: DesignFrame;
  private tab: EncyclopediaTab = 'microbes';
  private selected = '';
  private items: ListItem[] = [];
  private story: StoryOverlay | null = null;
  private ready = false;
  private readonly tabs = new Container();
  private readonly list = new Container();
  private readonly detail = new Container();
  private readonly stops: (() => void)[] = [];
  private readonly listeners: (() => void)[] = [];
  private readonly tall: boolean;
  private readonly L: Layout;
  /** Прокрутка списка и статьи на телефоне. */
  private listScroll: ScrollView | null = null;
  private detailScroll: ScrollView | null = null;

  constructor(private readonly options: EncyclopediaOptions) {
    const frame = currentFrame();
    this.tall = frame.width < frame.height;
    this.L = this.tall ? TALL : WIDE;
    this.view = new DesignFrame(screenBackground(0.7, 0.5, [], frame), frame);
    this.header();
    if (this.tall) {
      this.listScroll = new ScrollView(this.L.listW, this.L.listH);
      this.listScroll.position.set(this.L.listX, this.L.listY);
      this.listScroll.content.addChild(this.list);
      this.detailScroll = new ScrollView(this.L.detailW, this.L.detailH);
      this.detailScroll.position.set(this.L.detailX, this.L.detailY);
      this.detailScroll.content.addChild(this.detail);
      this.view.content.addChild(this.tabs, this.listScroll, this.detailScroll);
    } else {
      this.list.position.set(this.L.listX, this.L.listY);
      this.detail.position.set(this.L.detailX, this.L.detailY);
      this.view.content.addChild(this.tabs, this.list, this.detail);
    }
    this.listeners.push(listen('keydown', (e) => this.onKey(e)));
    const { catalog, scenes } = options.ctx.content;
    void loadArt(catalog, scenes).then(() => {
      if (this.view.destroyed) return;
      this.ready = true;
      this.pick(this.tab);
    });
  }

  private header(): void {
    const root = this.view.content;
    const back = new UiButton({
      width: 104,
      height: 44,
      radius: 12,
      label: 'Меню',
      fontSize: 15,
      center: true,
      icon: { key: 'icon-back', size: 18 },
      onTap: () => this.close(),
    });
    back.accessibleTitle = 'Закрыть энциклопедию';
    back.position.set(60, 22);
    const title = new Text({
      text: 'Энциклопедия',
      style: textStyle({ size: 30, display: true, weight: '900', shadow: 3 }),
    });
    title.anchor.set(0, 0.5);
    title.position.set(60 + 104 + 18, 44);
    const { opened, total } = openedCount(this.options.ctx.content, this.options.ctx.seen);
    const count = new Text({
      text: `Открыто ${opened} из ${total}`,
      style: textStyle({ size: 14, color: COLOR.textMuted }),
    });
    count.anchor.set(0, 0.5);
    count.position.set(title.x + title.width + 16, 46);
    root.addChild(back, title, count);
  }

  private renderTabs(): void {
    this.tabs.removeChildren().forEach((child) => child.destroy({ children: true }));
    const buttons = TABS.map(({ id, label }) => {
      const active = id === this.tab;
      const box = new Container();
      const t = new Text({
        text: label,
        style: textStyle({
          size: 14,
          display: true,
          weight: '800',
          color: active ? COLOR.ink : COLOR.textSoft,
        }),
      });
      const w = t.width + 36;
      const bg = new Graphics();
      if (active)
        bg.roundRect(0, 3, w, 42, 12).fill(COLOR.ink).roundRect(0, 0, w, 42, 12).fill(COLOR.mint);
      else bg.roundRect(0, 0, w, 42, 12).fill({ color: COLOR.ink, alpha: 0.001 });
      t.anchor.set(0.5);
      t.position.set(w / 2, 21);
      box.addChild(bg, t);
      box.eventMode = 'static';
      box.cursor = 'pointer';
      box.accessible = true;
      box.accessibleType = 'button';
      box.accessibleTitle = active ? `${label} (открыт)` : label;
      box.on('pointertap', () => this.pick(id));
      return { box, w };
    });
    const inner = buttons.reduce((sum, b) => sum + b.w, 0) + 6 * (buttons.length - 1);
    const width = inner + 10;
    this.tabs.addChild(
      new Graphics()
        .roundRect(0, 0, width, 52, 16)
        .fill({ color: COLOR.panelBottom, alpha: 0.85 })
        .stroke({ width: 2, color: COLOR.borderDim }),
    );
    let x = 5;
    for (const { box, w } of buttons) {
      box.position.set(x, 5);
      x += w + 6;
      this.tabs.addChild(box);
    }
    if (this.tall) {
      // Шесть вкладок шире телефона — ужимаем ряд целиком.
      this.tabs.scale.set(Math.min(1, this.L.listW / width));
      this.tabs.position.set(this.L.listX, 84);
    } else {
      this.tabs.position.set(1440 - 60 - width, 18);
    }
  }

  private pick(tab: EncyclopediaTab, selected?: string): void {
    this.tab = tab;
    this.items = listItems(this.options.ctx.content, this.options.ctx.seen, tab);
    this.selected = selected ?? this.items.find((i) => !i.locked)?.id ?? this.items[0]?.id ?? '';
    this.renderTabs();
    this.listScroll?.scrollTo(0);
    this.render();
  }

  private render(): void {
    if (!this.ready) return;
    this.stops.splice(0).forEach((stop) => stop());
    for (const box of [this.list, this.detail]) {
      box.removeChildren().forEach((child) => child.destroy({ children: true }));
    }
    this.renderList();
    this.renderDetail();
    this.listScroll?.refresh();
    // Новая статья открывается с начала.
    this.detailScroll?.scrollTo(0);
  }

  private renderList(): void {
    const items = this.items;
    if (items.length === 0) {
      const empty = new Text({
        text: 'Здесь пока пусто.',
        style: textStyle({ size: 16, color: COLOR.textMuted }),
      });
      this.list.addChild(empty);
      return;
    }
    const gap = 14;
    const rows = Math.ceil(items.length / 2);
    // На телефоне карточки постоянной высоты, список прокручивается.
    const h = this.tall ? 96 : Math.min(104, Math.floor((this.L.listH - gap * (rows - 1)) / rows));
    const w = (this.L.listW - gap) / 2;
    items.forEach((item, i) => {
      const card = this.card(item, w, h);
      card.position.set((i % 2) * (w + gap), Math.floor(i / 2) * (h + gap));
      this.list.addChild(card);
    });
  }

  private card(item: ListItem, w: number, h: number): Container {
    const selected = item.id === this.selected;
    const box = new Container();
    const bg = new Graphics();
    if (selected) {
      bg.roundRect(-3, -3, w + 6, h + 6, 21).fill(COLOR.ink);
      bg.roundRect(0, 0, w, h, 18)
        .fill(vertical(0x245678, 0x0e1c3a))
        .stroke({ width: 3, color: COLOR.mint });
    } else {
      bg.roundRect(0, 0, w, h, 18)
        .fill({ color: COLOR.panelBottom, alpha: 0.75 })
        .stroke({ width: 2, color: COLOR.borderDim });
    }
    box.addChild(bg);
    const icon = Math.min(72, h - 16);
    const scene = this.tab === 'archive';
    const artW = scene ? Math.round(icon * 1.6) : icon;
    if (!item.locked || item.art === 'boss-locked') {
      const art = new Sprite(artTexture(item.art));
      art.anchor.set(0.5);
      art.width = scene ? artW : icon * 0.86;
      art.height = scene ? artW * (SCENE_HEIGHT / SCENE_WIDTH) : icon * 0.86;
      art.position.set(10 + artW / 2, h / 2);
      box.addChild(art);
    } else {
      const lock = artSprite('icon-lock', icon * 0.4);
      lock.position.set(10 + artW / 2, h / 2);
      box.addChild(
        new Graphics()
          .roundRect(10, h / 2 - icon / 2, artW, icon, 14)
          .stroke({ width: 2, color: COLOR.borderDim, alpha: 0.8 }),
        lock,
      );
    }
    const tx = 10 + artW + 12;
    const name = new Text({
      text: item.name,
      style: textStyle({
        size: 15,
        display: true,
        weight: '800',
        wrap: w - tx - 12,
        lineHeight: 19,
      }),
    });
    const sub = new Text({
      text: item.sub,
      style: textStyle({ size: 13, color: COLOR.textMuted, wrap: w - tx - 12, lineHeight: 17 }),
    });
    const textH = name.height + 3 + sub.height;
    name.position.set(tx, (h - textH) / 2);
    sub.position.set(tx, name.y + name.height + 3);
    box.addChild(name, sub);
    if (item.locked) box.alpha = 0.6;
    box.eventMode = 'static';
    box.cursor = 'pointer';
    box.accessible = true;
    box.accessibleType = 'button';
    box.accessibleTitle = item.locked ? `${item.sub}` : `${item.name}. ${item.sub}`;
    box.on('pointertap', () => {
      this.selected = item.id;
      this.render();
    });
    return box;
  }

  private renderDetail(): void {
    const pad = this.L.pad;
    const item = this.items.find((i) => i.id === this.selected);
    if (!item) return;
    const body = new Container();
    body.position.set(pad, 28);
    this.detail.addChild(body);
    if (this.tab === 'archive') this.sceneDetail(body, item);
    else this.entryDetail(body, item);
    const note = new Text({
      text: 'Справка упрощена для игры и не является медицинской рекомендацией.',
      style: textStyle({ size: 12, color: COLOR.textFaint }),
    });
    // Панель по высоте статьи (на телефоне статья прокручивается), не ниже окна.
    const height = Math.max(this.L.detailH, 28 + body.height + 28 + note.height + 28);
    this.detail.addChildAt(panel({ width: this.L.detailW, height, radius: 24, alpha: 0.94 }), 0);
    note.position.set(pad, height - 28 - note.height);
    this.detail.addChild(note);
  }

  private entryDetail(body: Container, item: ListItem): void {
    const entry = this.options.ctx.content.catalog.find((e) => e.id === item.id);
    if (!entry) return;
    const boss = bossOf(entry);
    const inner = this.L.detailW - 2 * this.L.pad;

    // Портрет в круглом «окне» сосуда.
    const portrait = new Container();
    const ps = this.L.portrait;
    portrait.scale.set(ps);
    portrait.position.set(115 * ps, 115 * ps);
    portrait.addChild(
      new Graphics()
        .circle(0, 0, 118)
        .fill(COLOR.ink)
        .circle(0, 0, 115)
        .fill(
          new FillGradient({
            type: 'radial',
            center: { x: 0.5, y: 0.55 },
            innerRadius: 0,
            outerCenter: { x: 0.5, y: 0.55 },
            outerRadius: 0.5,
            colorStops: boss
              ? [
                  { offset: 0, color: 0x5a1e46 },
                  { offset: 1, color: 0x24091f },
                ]
              : [
                  { offset: 0, color: 0x135a55 },
                  { offset: 1, color: 0x0b2433 },
                ],
            textureSpace: 'local',
          }),
        )
        .stroke({ width: 4, color: 0xbef0ff, alpha: 0.3 }),
    );
    if (item.locked) {
      portrait.addChild(artSprite(boss ? 'boss-locked' : 'icon-lock', boss ? 180 : 70));
    } else {
      const microbe = /^microbe-(\d+)$/.exec(entry.id);
      const art = boss
        ? artSprite(`boss-${boss}`, 190)
        : microbe
          ? artSprite(`microbe-${microbe[1]}`, 170)
          : new Sprite(artTexture(item.art));
      if (!boss && !microbe) {
        art.anchor.set(0.5);
        art.width = 170;
        art.height = 170;
      }
      portrait.addChild(art);
      this.stops.push(
        boss
          ? idle(art, 'boss')
          : microbe
            ? microbeIdle(art, Number(microbe[1]))
            : idle(art, 'float'),
      );
    }
    body.addChild(portrait);

    // Заголовок справа от портрета.
    const tx = 230 * ps + 28;
    const headW = inner - tx;
    const head = new Container();
    head.x = tx;
    const facts = boss && !item.locked ? bossFacts(this.options.ctx.content, boss) : null;
    const kicker = caps(
      item.locked ? item.sub : KIND_KICKER[entry.kind],
      boss ? COLOR.pink : COLOR.mint,
    );
    const name = new Text({
      text: item.locked ? '???' : tr(entry.title),
      style: textStyle({
        size: 36,
        display: true,
        weight: '900',
        shadow: 3,
        wrap: headW,
        lineHeight: 42,
      }),
    });
    name.y = 22;
    head.addChild(kicker, name);
    let hy = name.y + name.height + 10;
    const tags = item.locked
      ? ['Ещё не встречено']
      : facts
        ? [...facts.tags]
        : [item.sub].filter((t) => t !== KIND_KICKER[entry.kind]);
    let tagX = 0;
    for (const label of tags) {
      const t = new Text({
        text: label,
        style: textStyle({ size: 13, weight: '500', color: 0xc4fff7 }),
      });
      const w = t.width + 24;
      if (tagX + w > headW) {
        tagX = 0;
        hy += 36;
      }
      const chip = new Container();
      chip.position.set(tagX, hy);
      chip.addChild(
        new Graphics()
          .roundRect(0, 0, w, 28, 14)
          .fill({ color: COLOR.mint, alpha: 0.12 })
          .stroke({ width: 1.5, color: COLOR.mint, alpha: 0.4 }),
      );
      t.position.set(12, 5);
      chip.addChild(t);
      head.addChild(chip);
      tagX += w + 8;
    }
    if (tags.length > 0) hy += 36;
    if (facts?.quote && boss) {
      const bubble = speechBubble(BOSS_NAMES[boss], facts.quote, Math.min(headW, 360), 'bottom');
      bubble.y = hy + 6;
      head.addChild(bubble);
      this.stops.push(idle(bubble, 'talk'));
      hy = bubble.y + bubble.height + 16;
    }
    head.y = Math.max(0, (230 * ps - hy) / 2);
    body.addChild(head);

    // «В игре» и «В жизни».
    let y = Math.max(230 * ps, head.y + hy) + 22;
    const game = item.locked
      ? 'Откроется, когда вы встретите это в экспедиции или в бою.'
      : `${tr(entry.what)}\n\nКак справиться: ${tr(entry.how)}`;
    const real = item.locked ? null : entry.fact ? tr(entry.fact) : null;
    const boxes: [string, number, string, string][] = [
      ['В игре', COLOR.gold, 'icon-play-gold', game],
    ];
    if (real) boxes.push(['В жизни', COLOR.mint, 'icon-search', real]);
    const gap = 16;
    const boxW = (inner - gap * (boxes.length - 1)) / boxes.length;
    const texts = boxes.map(
      ([, , , text]) =>
        new Text({
          text,
          style: textStyle({ size: 15, color: 0xe2f1f7, wrap: boxW - 36, lineHeight: 22 }),
        }),
    );
    const boxH = Math.max(...texts.map((t) => t.height)) + 16 + 26 + 16;
    boxes.forEach(([label, color, icon], i) => {
      const box = new Container();
      box.position.set(i * (boxW + gap), y);
      box.addChild(
        new Graphics()
          .roundRect(0, 0, boxW, boxH, 16)
          .fill({ color: COLOR.night, alpha: 0.45 })
          .stroke({ width: 2, color: COLOR.borderDim }),
      );
      const mark = artSprite(icon, 18);
      mark.position.set(18 + 9, 16 + 9);
      const title = caps(label, color);
      title.position.set(18 + 26, 18);
      const text = texts[i];
      if (text) {
        text.position.set(18, 16 + 26);
        box.addChild(text);
      }
      box.addChild(mark, title);
      body.addChild(box);
    });
    y += boxH + 20;

    if (item.locked) return;
    const microbe = /^microbe-(\d+)$/.exec(entry.id);
    if (microbe) {
      body.addChild(this.careChips(Number(microbe[1]), y));
    } else if (facts) {
      const section = caps(`Способности · здоровье ${facts.hp}`, COLOR.pink);
      section.y = y;
      body.addChild(section);
      let ay = y + 26;
      for (const ability of facts.abilities) {
        const text = new Text({
          text: ability.text,
          style: textStyle({ size: 13, color: 0xe2b8cc, wrap: inner - 76, lineHeight: 18 }),
        });
        const rowH = Math.max(54, 32 + text.height);
        const row = new Container();
        row.y = ay;
        row.addChild(
          new Graphics()
            .roundRect(0, 0, inner, rowH, 14)
            .fill({ color: 0x4a1638, alpha: 0.6 })
            .stroke({ width: 2, color: 0x5e2346 }),
          new Graphics()
            .roundRect(14, rowH / 2 - 17, 34, 34, 10)
            .fill(0xff4f7b)
            .stroke({ width: 2, color: COLOR.ink }),
        );
        const icon = artSprite(`icon-boss-${ability.kind}`, 18);
        icon.position.set(14 + 17, rowH / 2);
        const nameT = new Text({
          text: ability.name,
          style: textStyle({ size: 15, weight: '700' }),
        });
        nameT.position.set(62, 9);
        text.position.set(62, 30);
        row.addChild(icon, nameT, text);
        body.addChild(row);
        ay += rowH + 8;
      }
    }
  }

  /** «Чем лечить в игре»: что помогает (зелёные) и что бесполезно (розовые). */
  private careChips(color: number, y: number): Container {
    const box = new Container();
    box.y = y;
    box.addChild(caps('Чем лечить в игре', COLOR.textSoft));
    const { good, bad } = microbeCare(color);
    let x = 0;
    for (const [label, ok] of [
      ...good.map((g) => [g, true] as const),
      ...bad.map((b) => [b, false] as const),
    ]) {
      const t = new Text({ text: label, style: textStyle({ size: 14, weight: '500' }) });
      const w = t.width + 24 + 20;
      const chip = new Container();
      chip.position.set(x, 26);
      const accent = ok ? COLOR.teal : 0xff4f7b;
      const g = new Graphics()
        .roundRect(0, 0, w, 34, 10)
        .fill({ color: accent, alpha: ok ? 0.14 : 0.12 })
        .stroke({ width: 2, color: accent });
      if (ok) g.moveTo(12, 17.5).lineTo(16, 21.5).lineTo(23, 12.5);
      else g.moveTo(12, 11).lineTo(23, 22).moveTo(23, 11).lineTo(12, 22);
      g.stroke({ width: 3, color: ok ? COLOR.teal : COLOR.pink, cap: 'round', join: 'round' });
      t.position.set(32, 7);
      chip.addChild(g, t);
      box.addChild(chip);
      x += w + 8;
    }
    return box;
  }

  private sceneDetail(body: Container, item: ListItem): void {
    const inner = this.L.detailW - 2 * this.L.pad;
    const scene = this.options.ctx.content.scenes.find((s) => s.id === item.id);
    if (!scene) return;
    const kicker = caps(item.locked ? 'Архив лаборатории' : item.sub, COLOR.gold);
    const title = new Text({
      text: item.name,
      style: textStyle({ size: 32, display: true, weight: '900', shadow: 3, wrap: inner }),
    });
    title.y = 22;
    body.addChild(kicker, title);
    const y = title.y + title.height + 18;
    const h = Math.round((inner * SCENE_HEIGHT) / SCENE_WIDTH);
    const frame = new Graphics()
      .roundRect(-3, y - 3, inner + 6, h + 6, 21)
      .fill(COLOR.ink)
      .roundRect(0, y, inner, h, 18)
      .fill(COLOR.night);
    body.addChild(frame);
    if (item.locked) {
      const lock = artSprite('icon-lock', 64);
      lock.position.set(inner / 2, y + h / 2);
      const text = new Text({
        text: 'Сцена откроется по ходу экспедиции.',
        style: textStyle({ size: 16, color: COLOR.textMuted }),
      });
      text.anchor.set(0.5, 0);
      text.position.set(inner / 2, y + h + 20);
      body.addChild(lock, text);
      return;
    }
    const picture = new Sprite(artTexture(item.art));
    picture.position.set(0, y);
    picture.width = inner;
    picture.height = h;
    const mask = new Graphics().roundRect(0, y, inner, h, 18).fill(COLOR.white);
    picture.mask = mask;
    const frames = scene.frames.length;
    const info = new Text({
      text: `${frames} ${frames === 1 ? 'кадр' : frames < 5 ? 'кадра' : 'кадров'} · можно пересмотреть`,
      style: textStyle({ size: 15, color: COLOR.textSoft }),
    });
    info.position.set(0, y + h + 22);
    const watch = new UiButton({
      width: 220,
      height: 56,
      label: 'Смотреть',
      variant: 'primary',
      center: true,
      fontSize: 18,
      icon: { key: 'icon-play-ink', size: 20 },
      onTap: () => this.replay(scene),
    });
    watch.position.set(inner - 220, y + h + 14);
    body.addChild(picture, mask, info, watch);
  }

  private replay(scene: SceneView): void {
    this.story?.destroy();
    const story = new StoryOverlay({
      content: this.options.ctx.content,
      scene,
      reducedMotion: this.options.reducedMotion,
    });
    this.story = story;
    void story.done.then(() => {
      if (this.story === story) this.story = null;
    });
  }

  /** Esc — закрыть; стрелки — по карточкам; Q / E — соседний раздел; Enter в архиве — смотреть. */
  private onKey(e: KeyboardEvent): void {
    if (this.story || !this.ready) return;
    e.stopImmediatePropagation();
    if (e.code === 'Escape') {
      this.close();
      return;
    }
    const tabIndex = TABS.findIndex((t) => t.id === this.tab);
    if (e.code === 'KeyQ' || e.code === 'KeyE') {
      const next = TABS[(tabIndex + (e.code === 'KeyE' ? 1 : TABS.length - 1)) % TABS.length];
      if (next) this.pick(next.id);
      return;
    }
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -2, ArrowDown: 2 }[e.code];
    if (step !== undefined) {
      e.preventDefault();
      const index = this.items.findIndex((i) => i.id === this.selected);
      const next = this.items[Math.max(0, Math.min(this.items.length - 1, index + step))];
      if (next) {
        this.selected = next.id;
        this.render();
      }
      return;
    }
    if (e.code === 'Enter' && this.tab === 'archive') {
      const scene = this.options.ctx.content.scenes.find((s) => s.id === this.selected);
      const item = this.items.find((i) => i.id === this.selected);
      if (scene && item && !item.locked) this.replay(scene);
    }
  }

  private close(): void {
    this.options.onClose();
  }

  destroy(): void {
    this.story?.destroy();
    this.listeners.splice(0).forEach((off) => off());
    this.stops.splice(0).forEach((stop) => stop());
    this.view.destroy();
  }
}
